<template>
  <div class="login-page d-flex flex-column flex-lg-row flex-column-fluid">
    <!--begin::Aside-->
    <div class="login-aside d-flex flex-column flex-lg-row-auto w-xl-600px position-xl-relative">
      <div class="d-flex flex-column position-xl-fixed top-0 bottom-0 w-xl-600px scroll-y">
        <div class="login-aside-inner d-flex flex-column flex-center flex-grow-1 p-10 pt-lg-20">
          <h1 class="fw-bolder fs-2qx pb-5 pb-md-8 login-title">{{ $t('welcome') }}</h1>
          <p class="fw-bold fs-2 login-subtitle">
            {{ $t('welcome_text_start') }}
            <br /> {{ $t('welcome_text_end') }}
          </p>
          <div class="login-hero d-flex justify-content-center align-items-center mt-10">
            <img alt="Logo" src="/assets/media/r2b_login.jpg" class="login-hero-img" />
          </div>
        </div>
        <div class="login-aside-logo">
          <img alt="Qomerxia" src="/assets/media/logos/qomerxia-logo.png" class="h-20px" />
        </div>
      </div>
    </div>
    <!--end::Aside-->

    <!--begin::Body-->
    <div class="login-body d-flex flex-column flex-lg-row-fluid">
      <!--begin::Language selector-->
      <div class="login-lang d-flex justify-content-end p-5">
        <div class="btn btn-icon btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px w-md-40px h-md-40px"
             data-kt-menu-trigger="hover" data-kt-menu-attach="parent" data-kt-menu-placement="bottom-end">
          <img class="h-20px w-20px rounded-sm" :src="currentLanguage.img" alt="" />
        </div>
        <div class="menu menu-sub menu-sub-dropdown menu-column w-150px" data-kt-menu="true">
          <div v-for="lang in langs" :key="lang.code" class="menu-item px-3">
            <a @click.prevent="changeLang(lang)" href="#" class="menu-link d-flex px-5 active">
              <span class="symbol symbol-20px me-4">
                <img class="rounded-1" :src="lang.img" :alt="$t(lang.title)" />
              </span>{{ $t(lang.title) }}
            </a>
          </div>
        </div>
      </div>
      <!--end::Language selector-->

      <!--begin::Form section-->
      <div class="login-form-section d-flex flex-center flex-column flex-grow-1">
        <div class="login-form-card w-lg-500px p-10 p-lg-15 mx-auto">
          <form id="kt_sign_in_form" class="form w-100" novalidate="novalidate"
                @submit.prevent="validateBeforeSubmit">
            <!--begin::Heading-->
            <div class="login-heading text-center mb-11">
              <a href="">
                <img alt="Logo" src="/assets/media/logos/qomerxia-logo.png" class="login-form-logo h-45px mb-8" />
              </a>
              <h1 class="text-dark fw-bolder fs-2 mb-1">{{ $t('auth.login') }}</h1>
              <div class="login-separator my-6"></div>
            </div>
            <!--end::Heading-->

            <!--begin::Email or phone-->
            <div class="fv-row mb-8">
              <label class="form-label fs-6 fw-bolder text-dark mb-2">{{ $t('auth.email_or_phone') }}</label>

              <div v-if="loginMode === 'email'" class="position-relative">
                <span class="login-input-icon login-input-icon-clickable login-input-icon-right"
                      :title="$t('auth.login_with_phone')"
                      @click="toggleLoginMode">
                  <i class="bi bi-telephone fs-4"></i>
                </span>
                <input v-model="loginData.email" autocomplete="off"
                       class="form-control form-control-lg form-control-solid login-input login-input-right-icon"
                       name="email" type="text" :placeholder="$t('auth.email')" />
              </div>

              <div v-else class="position-relative">
                <span class="login-input-icon login-input-icon-clickable login-input-icon-right"
                      :title="$t('auth.login_with_email')"
                      @click="toggleLoginMode">
                  <i class="bi bi-envelope fs-4"></i>
                </span>
                <InputTel
                    name="login_phone"
                    containerClass=""
                    v-model:phone="loginData.phone"
                    v-model:phone-code="loginData.phone_code"
                    :placeholder="$t('auth.phone')"
                    :isRequired="false"
                />
              </div>
            </div>
            <!--end::Email or phone-->

            <!--begin::Password-->
            <div class="fv-row mb-8">
              <div class="d-flex flex-stack mb-2">
                <label class="form-label fw-bolder text-dark fs-6 mb-0">{{ $t('auth.password') }}</label>
                <router-link :to="{name: 'forgot-password'}" class="link-primary fs-6 fw-bold">
                  {{ $t('auth.forgot_password') }}
                </router-link>
              </div>
              <div class="position-relative">
                <span class="login-input-icon">
                  <i class="bi bi-lock fs-4"></i>
                </span>
                <input v-model="loginData.password" autocomplete="off"
                       class="form-control form-control-lg form-control-solid login-input login-input-password"
                       name="password" :type="showPassword ? 'text' : 'password'" :placeholder="$t('auth.password')" />
                <span class="login-eye-icon" @click="showPassword = !showPassword">
                  <i :class="showPassword ? 'bi bi-eye' : 'bi bi-eye-slash'" class="fs-4"></i>
                </span>
              </div>
            </div>
            <!--end::Password-->

            <!--begin::Submit-->
            <div class="d-grid mt-10">
              <button id="kt_sign_in_submit" :disabled="isLoading"
                      class="btn btn-lg btn-primary login-btn"
                      type="submit">
                <span class="indicator-label fw-bold">{{ $t('auth.login') }}</span>
                <span class="indicator-progress">{{ $t('loading') }}
                  <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>
            </div>
            <!--end::Submit-->
          </form>
        </div>
      </div>
      <!--end::Form section-->

      <!--begin::Footer-->
      <div class="login-footer d-flex flex-center py-5">
        <span class="text-muted fs-7">&copy; {{ new Date().getFullYear() }}</span>
      </div>
      <!--end::Footer-->
    </div>
    <!--end::Body-->
  </div>
</template>

<script>
// @ is an alias to /src
import {mapActions} from 'vuex'
import InputTel from '@/components/InputTel.vue'

export default {
  name: 'ConnexionView',
  components: {
    InputTel,
  },
  data() {
    return {
      loginMode: 'email',
      loginData: {
        email: '',
        phone: '',
        phone_code: '',
        password: ''
      },
      showPassword: false,
      isLoading: false,
      langs: {
        en : {
          'img': '/assets/media/united-states.svg',
          'title': 'header.language.english',
          'code': 'en'
        },
        fr : {
          'img': '/assets/media/france.svg',
          'title': 'header.language.french',
          'code': 'fr'
        },
      }
    }
  },
  computed : {
    currentLanguage(){
      return this.langs[this.$i18n?.locale]
    },
  },
  methods: {
    ...mapActions('auth', [
      'login',
      'loginByPhone'
    ]),

    changeLang(lang){
      this.$i18n.locale = lang.code
      localStorage.setItem('locale', lang.code)
    },

    toggleLoginMode() {
      if (this.loginMode === 'email') {
        this.loginMode = 'phone'
        this.loginData.email = ''
      } else {
        this.loginMode = 'email'
        this.loginData.phone = ''
        this.loginData.phone_code = ''
      }
    },

    async validateBeforeSubmit() {
      this.$loading('kt_sign_in_submit')
      this.isLoading = true
      const authenticate = this.loginMode === 'email' ? this.login(this.loginData) : this.loginByPhone(this.loginData)
      authenticate.then(async (res) => {
        window.toastr['success'](i18n.global.t('notification.logged'))
        const { resolveHomeRoute } = await import('@/helpers/homeRoute')
        const route = await resolveHomeRoute()
        this.$router.push(route)

        this.$stopLoading('kt_sign_in_submit')
        this.isLoading = false
      }).catch(() => {

        this.$stopLoading('kt_sign_in_submit')
        this.isLoading = false
      })
    }
  },
  updated() {
    this.$nextTick(() => {
      KTApp.init();
    })
  },
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
}

/* Aside panel */
.login-aside {
  background: linear-gradient(135deg, rgba(var(--platform-primary-rgb, 11, 61, 135), 0.06) 0%, #fff 50%, rgba(var(--platform-primary-rgb, 11, 61, 135), 0.1) 100%);
  height: 100vh;
  box-shadow: 4px 0 24px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.15);
}

.login-title {
  color: #1a1a2e;
}

.login-subtitle {
  color: #4a4a68;
  line-height: 1.6;
}

.login-hero-img {
  max-height: 360px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.2);
  object-fit: cover;
}

.login-aside-logo {
  position: absolute;
  bottom: 24px;
  right: 24px;
  opacity: 0.7;
}

/* Form card */
.login-form-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
}

.login-separator {
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--platform-primary, #0b3d87), transparent);
  border-radius: 1px;
}

/* Input with icon */
.login-input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #b5b5c3;
  z-index: 3;
  pointer-events: none;
}

.login-input-icon-clickable {
  pointer-events: auto;
  cursor: pointer;
  color: var(--platform-primary, #0b3d87);
  background: #ffffff;
  border: 1px solid rgba(var(--platform-primary-rgb, 11, 61, 135), 0.4);
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.login-input-icon-clickable:hover {
  background: var(--platform-primary, #0b3d87) !important;
  color: #ffffff !important;
  transform: translateY(-50%) scale(1.05);
}

.login-input-icon-clickable:hover i {
  color: #ffffff !important;
}

.login-input-icon-right {
  left: auto;
  right: 8px;
}

.login-input {
  padding-left: 42px !important;
  border-radius: 16px !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.login-input-right-icon {
  padding-left: 16px !important;
  padding-right: 56px !important;
}

.login-input:focus {
  border-color: var(--platform-primary, #0b3d87) !important;
  box-shadow: 0 0 0 3px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.15) !important;
}

:deep(.vue-tel-input) {
  border-radius: 16px !important;
  overflow: hidden;
}

:deep(.vti__input) {
  padding-right: 56px !important;
}

:deep(.vue-tel-input:focus-within) {
  border-color: var(--platform-primary, #0b3d87) !important;
  box-shadow: 0 0 0 3px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.15) !important;
}

/* Eye toggle icon */
.login-eye-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #b5b5c3;
  cursor: pointer;
  z-index: 3;
  transition: color 0.2s ease;
}

.login-eye-icon:hover {
  color: var(--platform-primary, #0b3d87);
}

.login-input-password {
  padding-right: 42px !important;
}

/* Submit button */
.login-btn {
  border-radius: 10px !important;
  padding: 12px 0 !important;
  font-size: 1.1rem !important;
  letter-spacing: 0.3px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(var(--platform-primary-rgb, 11, 61, 135), 0.35);
}

.login-btn:active:not(:disabled) {
  transform: translateY(0);
}

/* Responsive */
/* Ordinateur : tout tient dans la hauteur de l'écran (pas de scroll) ; le logo du panneau
   suit le contenu au lieu d'être posé par-dessus l'image */
@media (min-width: 992px) {
  .login-page {
    height: 100vh;
  }

  .login-aside-inner {
    padding: 2rem 3rem 1rem !important;
  }

  .login-title {
    font-size: 2rem !important;
    padding-bottom: 0.75rem !important;
  }

  .login-subtitle {
    font-size: 1.15rem !important;
    margin-bottom: 0;
  }

  .login-hero {
    margin-top: 1.75rem !important;
  }

  .login-hero-img {
    max-height: min(300px, 34vh);
    max-width: 100%;
  }

  .login-aside-logo {
    position: static;
    align-self: flex-end;
    padding: 0 24px 20px;
  }

  .login-lang {
    padding: 1rem 1.25rem 0 !important;
  }

  .login-form-card {
    padding: 2.5rem 3rem !important;
  }

  .login-form-logo {
    height: 38px !important;
    margin-bottom: 1.25rem !important;
  }

  .login-heading {
    margin-bottom: 1.5rem !important;
  }

  .login-heading .login-separator {
    margin: 1rem 0 0 !important;
  }

  .login-form-card .mb-8 {
    margin-bottom: 1.25rem !important;
  }

  .login-form-card .mt-10 {
    margin-top: 1.75rem !important;
  }

  .login-footer {
    padding: 0.75rem 0 !important;
  }
}

/* Ordinateur portable à écran bas (≤ 760 px de haut) : on resserre encore */
@media (min-width: 992px) and (max-height: 760px) {
  .login-title {
    font-size: 1.75rem !important;
  }

  .login-hero-img {
    max-height: 26vh;
  }

  .login-form-card {
    padding: 2rem 2.5rem !important;
  }
}

/* Mobile et tablette : formulaire seul, sur la hauteur visible de l'écran (100dvh exclut la
   barre d'adresse, contrairement à 100vh) — le panneau d'accueil et son image sont masqués */
@media (max-width: 991.98px) {
  .login-page {
    min-height: 100dvh;
  }

  .login-aside {
    display: none !important;
  }

  /* formulaire centré dans l'écran, en hauteur comme en largeur : la zone prend toute la
     hauteur, et le choix de langue comme le pied de page sortent du flux pour ne pas le décaler */
  .login-body {
    flex: 1 1 auto;
    position: relative;
    min-height: 100dvh;
  }

  .login-lang {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
  }

  .login-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .login-form-section {
    padding: 4.5rem 1rem;
  }

  .login-form-card {
    box-shadow: none;
    padding: 1.5rem !important;
    width: 100%;
    max-width: 440px;
  }

  .login-form-card .mb-11 {
    margin-bottom: 1.5rem !important;
  }

  .login-form-card .mb-8 {
    margin-bottom: 1.25rem !important;
  }

  .login-form-card .mt-10 {
    margin-top: 1.75rem !important;
  }
}
</style>
